import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";
import ErrorBoundary from "@/components/ErrorBoundary";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import OnboardingGuide from "@/components/OnboardingGuide";
import ConciergeWidget from "@/components/ConciergeWidget";
import ExitIntentModal from "@/components/ExitIntentModal";
import PwaInstallBanner from "@/components/PwaInstallBanner";
import LatestPremiumTicker from "@/components/LatestPremiumTicker";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "@/pages/Home";
import Directory from "@/pages/Directory";
import WhitePages from "@/pages/WhitePages";
import BusinessDetail from "@/pages/BusinessDetail";
import Search from "@/pages/Search";
import Pricing from "@/pages/Pricing";
import PourquoiPartenaire from "@/pages/PourquoiPartenaire";
import Login, { AuthVerify } from "@/pages/Login";
import Register from "@/pages/Register";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import { MentionsLegales, CGU, CGV, RGPD, Cookies, About } from "@/pages/Legal";
import { CityPage, CategoryPage, CityCategoryPage, BorderPage } from "@/pages/SeoPages";
import SeoCityCategoryPage from "@/pages/SeoCityCategoryPage";
import NearbyPage from "@/pages/NearbyPage";
import ReferralLanding from "@/pages/ReferralLanding";
import TeleproLogin from "@/pages/TeleproLogin";
import TeleproPortal from "@/pages/TeleproPortal";
import TeleproSignup from "@/pages/TeleproSignup";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { initAnalytics, trackPageView } from "@/lib/analytics";

function AnalyticsListener() {
  const location = useLocation();
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => { trackPageView(location.pathname + location.search); }, [location.pathname, location.search]);
  return null;
}

function Layout() {
  const location = useLocation();
  // Hide consumer-facing floating widgets (concierge chat, "Comment ça marche?",
  // exit-intent modal, PWA banner, premium ticker) on internal employee routes
  // where they overlap critical UI on mobile and aren't useful.
  const isInternalRoute = location.pathname.startsWith("/teleprospecteur");
  return (
    <div className="min-h-screen flex flex-col">
      <AnalyticsListener />
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <CookieBanner />
      {!isInternalRoute && (
        <>
          <OnboardingGuide />
          <ConciergeWidget />
          <ExitIntentModal />
          <PwaInstallBanner />
          <LatestPremiumTicker />
        </>
      )}
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/annuaire" element={<Directory />} />
              <Route path="/pages-jaunes" element={<Directory />} />
              <Route path="/pages-blanches" element={<WhitePages />} />
              <Route path="/entreprise/:slug" element={<BusinessDetail />} />
              <Route path="/recherche" element={<Search />} />
              <Route path="/passez-partenaire" element={<Pricing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/pourquoi-partenaire" element={<PourquoiPartenaire />} />
              <Route path="/connexion" element={<Login />} />
              <Route path="/inscription" element={<Register />} />
              <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
              <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
              <Route path="/auth/verify" element={<AuthVerify />} />
              <Route path="/dashboard" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
              <Route path="/admin" element={<ErrorBoundary><Admin /></ErrorBoundary>} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/cgu" element={<CGU />} />
              <Route path="/cgv" element={<CGV />} />
              <Route path="/rgpd" element={<RGPD />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/a-propos" element={<About />} />
              <Route path="/villes/:city" element={<CityPage />} />
              <Route path="/villes/:city/:category" element={<CityCategoryPage />} />
              <Route path="/categories/:category" element={<CategoryPage />} />
              {/* Canonical directory URLs — keep /villes/... as legacy aliases */}
              <Route path="/annuaire/:city" element={<CityPage canonicalPrefix="/annuaire" />} />
              <Route path="/annuaire/:citySlug/:categorySlug" element={<SeoCityCategoryPage />} />
              <Route path="/frontalier-suisse" element={<BorderPage kind="suisse" />} />
              <Route path="/frontalier-allemand" element={<BorderPage kind="allemand" />} />
              <Route path="/autour-de-moi" element={<NearbyPage />} />
              <Route path="/parrainage/:code" element={<ReferralLanding />} />
              <Route path="/teleprospecteur" element={<TeleproLogin />} />
              <Route path="/teleprospecteur/inscription" element={<TeleproSignup />} />
              <Route path="/teleprospecteur/portail" element={<TeleproPortal />} />
              <Route path="*" element={<div className="p-20 text-center"><div className="label-uppercase mb-2">404</div><h1 className="font-display text-3xl">Page introuvable</h1></div>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
    </HelmetProvider>
  );
}
